import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { ShoppingCart, X, Trash2, Plus, Minus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CartDrawer = () => {
  const { language } = useLanguage();
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount, clearCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const content = {
    en: {
      title: "Shopping Cart",
      empty: "Your cart is empty",
      emptyDesc: "Add some courses to get started!",
      subtotal: "Subtotal",
      checkout: "Proceed to Checkout",
      continueShopping: "Continue Shopping",
      remove: "Remove",
      processing: "Processing..."
    },
    ar: {
      title: "سلة التسوق",
      empty: "سلتك فارغة",
      emptyDesc: "أضف بعض الدورات للبدء!",
      subtotal: "المجموع الفرعي",
      checkout: "إتمام الطلب",
      continueShopping: "متابعة التسوق",
      remove: "إزالة",
      processing: "جارٍ المعالجة..."
    }
  };

  const t = content[language];

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    setIsProcessing(true);

    try {
      // Prepare cart items for backend
      const items = cart.map(item => ({
        course_id: item.id,
        course_title: item.title,
        price: item.price.current,
        currency: item.price.currency.en,
        quantity: item.quantity
      }));

      // Get current origin URL
      const originUrl = window.location.origin;

      // Create checkout session
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/session`,
        {
          items,
          origin_url: originUrl,
          user_email: null, // Could be from user profile if logged in
          user_name: null
        }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(language === 'en' 
        ? 'Failed to process checkout. Please try again.' 
        : 'فشل في معالجة الطلب. يرجى المحاولة مرة أخرى.');
      setIsProcessing(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button data-testid="cart-icon-button" className="relative p-2 hover:bg-gray-100 rounded-full transition-colors">
          <ShoppingCart className="w-6 h-6 text-gray-700" />
          {getCartCount() > 0 && (
            <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-teal-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {getCartCount()}
            </span>
          )}
        </button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="text-2xl font-bold">{t.title}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full mt-6">
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <ShoppingCart className="w-24 h-24 text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.empty}</h3>
              <p className="text-gray-600">{t.emptyDesc}</p>
              <Button 
                onClick={() => setIsOpen(false)}
                className="mt-6 bg-teal-600 hover:bg-teal-700"
              >
                {t.continueShopping}
              </Button>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex gap-4">
                      {/* Course Image */}
                      <img
                        src={item.image}
                        alt={item.title[language]}
                        className="w-24 h-24 object-cover rounded-lg"
                      />

                      {/* Course Details */}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                          {item.title[language]}
                        </h4>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xl font-bold text-teal-600">
                              {item.price.current}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">
                              {item.price.currency[language]}
                            </span>
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-semibold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="mt-2 text-sm text-red-600 hover:text-red-700 flex items-center space-x-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{t.remove}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="border-t pt-6 mt-6 space-y-4">
                <div className="flex justify-between items-center text-lg">
                  <span className="font-semibold">{t.subtotal}:</span>
                  <span className="text-2xl font-bold text-teal-600">
                    {getCartTotal()} {cart[0]?.price.currency[language]}
                  </span>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={isProcessing}
                  data-testid="proceed-checkout-btn"
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6 rounded-xl"
                >
                  {isProcessing ? t.processing : t.checkout}
                </Button>

                <Button
                  onClick={() => setIsOpen(false)}
                  variant="outline"
                  className="w-full"
                >
                  {t.continueShopping}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
